import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { itinerariesService } from '../../services/itinerariesService';
import EditItineraryModal from './EditItineraryModal';
import AddCommissionModal from './AddCommissionModal';
import './ItinerariesList.css';

const ItinerariesList = () => {
  const navigate = useNavigate();
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [selectedItinerary, setSelectedItinerary] = useState(null);
  const [showSendUpdateModal, setShowSendUpdateModal] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [sendingUpdate, setSendingUpdate] = useState(false);

  useEffect(() => {
    fetchItineraries();
  }, []);

  const fetchItineraries = async () => {
    try {
      setLoading(true);
      const data = await itinerariesService.getAll();
      // Ensure data is an array
      const itinerariesArray = Array.isArray(data) ? data : data.data || [];
      setItineraries(itinerariesArray);
    } catch (error) {
      console.error('Error fetching itineraries:', error);
      setError('Failed to load itineraries');
    } finally {
      setLoading(false);
    }
  };

  const getStatus = (itinerary) => {
    if (!itinerary.travel_start_date || !itinerary.travel_end_date) {
      return 'upcoming';
    }
    
    const now = new Date();
    const startDate = new Date(itinerary.travel_start_date);
    const endDate = new Date(itinerary.travel_end_date);
    
    if (now < startDate) return 'upcoming';
    if (now > endDate) return 'completed';
    return 'active';
  };

  const getCommissionStatus = (itinerary) => {
    // Check if travel has completed
    const endDate = new Date(itinerary.travel_end_date);
    const now = new Date();
    
    if (now < endDate) {
      return null; // Travel not completed yet
    }
    
    // Calculate days since travel ended
    const daysSinceEnd = Math.floor((now - endDate) / (1000 * 60 * 60 * 24));
    
    if (itinerary.commission_status === 'submitted') {
      return { text: 'Submitted', className: 'status-submitted' };
    } else if (itinerary.commission_status === 'approved') {
      return { text: 'Approved', className: 'status-approved' };
    } else if (daysSinceEnd > 60) {
      return { text: 'Overdue', className: 'status-overdue' };
    } else {
      return { text: 'Needs Submission', className: 'status-pending' };
    }
  };

  const getClientName = (itinerary) => {
    // Try multiple ways to get the client name
    if (itinerary.first_name && itinerary.last_name) {
      return `${itinerary.first_name} ${itinerary.last_name}`;
    }
    
    if (itinerary.client_name) {
      return itinerary.client_name;
    }
    
    // Try to parse from lead_traveler if it exists
    if (itinerary.lead_traveler) {
      try {
        const leadTraveler = typeof itinerary.lead_traveler === 'string' 
          ? JSON.parse(itinerary.lead_traveler) 
          : itinerary.lead_traveler;
        
        const firstName = leadTraveler.firstName || leadTraveler.first_name || '';
        const lastName = leadTraveler.lastName || leadTraveler.last_name || '';
        
        if (firstName || lastName) {
          return `${firstName} ${lastName}`.trim();
        }
      } catch (e) {
        console.error('Error parsing lead_traveler:', e);
      }
    }
    
    return 'Unknown Client';
  };

  const handleEditItinerary = async (itinerary) => {
    // Fetch full itinerary details
    try {
      const fullItinerary = await itinerariesService.getById(itinerary.id);
      setSelectedItinerary(fullItinerary);
      setShowEditModal(true);
    } catch (error) {
      console.error('Error fetching itinerary details:', error);
      alert('Failed to load itinerary details');
    }
  };

  const handleAddCommission = async (itinerary) => {
    // Fetch full itinerary details
    try {
      const fullItinerary = await itinerariesService.getById(itinerary.id);
      setSelectedItinerary(fullItinerary);
      setShowCommissionModal(true);
    } catch (error) {
      console.error('Error fetching itinerary details:', error);
      alert('Failed to load itinerary details');
    }
  };

  const handleSendUpdate = (itinerary) => {
    setSelectedItinerary(itinerary);
    setUpdateMessage('');
    setShowSendUpdateModal(true);
  };

  const handleSendUpdateSubmit = async () => {
    if (!updateMessage.trim()) {
      alert('Please enter a message about the update');
      return;
    }

    setSendingUpdate(true);
    try {
      // Send update email to client (this also adds the version entry)
      await itinerariesService.sendUpdate(selectedItinerary.id, {
        message: updateMessage
      });

      alert('Update sent to client successfully!');
      setShowSendUpdateModal(false);
      setUpdateMessage('');
      fetchItineraries(); // Refresh the list
    } catch (error) {
      console.error('Error sending update:', error);
      alert('Failed to send update. Please try again.');
    } finally {
      setSendingUpdate(false);
    }
  };

  // Filter itineraries
  const filteredItineraries = itineraries.filter(itinerary => {
    const clientName = getClientName(itinerary);
    const matchesSearch = 
      itinerary.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      clientName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const status = getStatus(itinerary);
    const matchesStatus = statusFilter === 'all' || status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Group itineraries by status
  const groupedItineraries = {
    upcoming: filteredItineraries.filter(i => getStatus(i) === 'upcoming'),
    active: filteredItineraries.filter(i => getStatus(i) === 'active'),
    completed: filteredItineraries.filter(i => getStatus(i) === 'completed')
  };

  if (loading) {
    return <div className="loading">Loading itineraries...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="itineraries-list">
      <div className="itineraries-header">
        <h1>Travel Itineraries</h1>
        <div className="header-stats">
          <div className="stat-badge">
            <span className="stat-label">Upcoming</span>
            <span className="stat-value">{groupedItineraries.upcoming.length}</span>
          </div>
          <div className="stat-badge">
            <span className="stat-label">Active</span>
            <span className="stat-value">{groupedItineraries.active.length}</span>
          </div>
          <div className="stat-badge">
            <span className="stat-label">Completed</span>
            <span className="stat-value">{groupedItineraries.completed.length}</span>
          </div>
        </div>
      </div>

      <div className="itineraries-filters">
        <input
          type="text"
          placeholder="Search by title or client name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="status-filter"
        >
          <option value="all">All Status</option>
          <option value="upcoming">Upcoming</option>
          <option value="active">Currently Traveling</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {/* Upcoming Travel */}
      {groupedItineraries.upcoming.length > 0 && (
        <div className="itinerary-section">
          <h2 className="section-title">
            <span className="section-icon">📅</span>
            Upcoming Travel
          </h2>
          <div className="itinerary-grid">
            {groupedItineraries.upcoming.map(itinerary => (
              <div key={itinerary.id} className="itinerary-card upcoming">
                <div className="itinerary-header">
                  <h3>{itinerary.title}</h3>
                  <span className="status-badge status-upcoming">Upcoming</span>
                </div>
                <div className="itinerary-details">
                  <p className="client-name">
                    <strong>Client:</strong> {getClientName(itinerary)}
                  </p>
                  <p className="travel-dates">
                    <strong>Travel Dates:</strong> {' '}
                    {new Date(itinerary.travel_start_date).toLocaleDateString()} - 
                    {new Date(itinerary.travel_end_date).toLocaleDateString()}
                  </p>
                  <p className="total-amount">
                    <strong>Total:</strong> ${parseFloat(itinerary.total_amount || 0).toFixed(2)}
                  </p>
                  {itinerary.payment_status && (
                    <p className="payment-status">
                      <strong>Payment:</strong> {itinerary.payment_status.replace('_', ' ')}
                    </p>
                  )}
                </div>
                <div className="itinerary-actions">
                  <button
                    className="btn btn-primary"
                    onClick={() => handleEditItinerary(itinerary)}
                  >
                    Edit Details
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => handleAddCommission(itinerary)}
                  >
                    Add Commission
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => handleSendUpdate(itinerary)}
                  >
                    Send Update
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Currently Traveling */}
      {groupedItineraries.active.length > 0 && (
        <div className="itinerary-section">
          <h2 className="section-title">
            <span className="section-icon">✈️</span>
            Currently Traveling
          </h2>
          <div className="itinerary-grid">
            {groupedItineraries.active.map(itinerary => (
              <div key={itinerary.id} className="itinerary-card active">
                <div className="itinerary-header">
                  <h3>{itinerary.title}</h3>
                  <span className="status-badge status-active">Active</span>
                </div>
                <div className="itinerary-details">
                  <p className="client-name">
                    <strong>Client:</strong> {getClientName(itinerary)}
                  </p>
                  <p className="travel-dates">
                    <strong>Travel Dates:</strong> {' '}
                    {new Date(itinerary.travel_start_date).toLocaleDateString()} - 
                    {new Date(itinerary.travel_end_date).toLocaleDateString()}
                  </p>
                  <p className="total-amount">
                    <strong>Total:</strong> ${parseFloat(itinerary.total_amount || 0).toFixed(2)}
                  </p>
                </div>
                <div className="itinerary-actions">
                  <button
                    className="btn btn-primary"
                    onClick={() => handleEditItinerary(itinerary)}
                  >
                    View Details
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => handleAddCommission(itinerary)}
                  >
                    Add Commission
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completed Travel */}
      {groupedItineraries.completed.length > 0 && (
        <div className="itinerary-section">
          <h2 className="section-title">
            <span className="section-icon">✅</span>
            Completed Travel
          </h2>
          <div className="itinerary-grid">
            {groupedItineraries.completed.map(itinerary => {
              const commissionStatus = getCommissionStatus(itinerary);
              return (
                <div key={itinerary.id} className="itinerary-card completed">
                  <div className="itinerary-header">
                    <h3>{itinerary.title}</h3>
                    <span className="status-badge status-completed">Completed</span>
                  </div>
                  <div className="itinerary-details">
                    <p className="client-name">
                      <strong>Client:</strong> {getClientName(itinerary)}
                    </p>
                    <p className="travel-dates">
                      <strong>Travel Dates:</strong> {' '}
                      {new Date(itinerary.travel_start_date).toLocaleDateString()} - 
                      {new Date(itinerary.travel_end_date).toLocaleDateString()}
                    </p>
                    <p className="total-amount">
                      <strong>Total:</strong> ${parseFloat(itinerary.total_amount || 0).toFixed(2)}
                    </p>
                    {commissionStatus && (
                      <p className="commission-status">
                        <strong>Commission:</strong>{' '}
                        <span className={`commission-badge ${commissionStatus.className}`}>
                          {commissionStatus.text}
                        </span>
                      </p>
                    )}
                  </div>
                  <div className="itinerary-actions">
                    <button
                      className="btn btn-primary"
                      onClick={() => handleEditItinerary(itinerary)}
                    >
                      View Details
                    </button>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => handleAddCommission(itinerary)}
                    >
                      Commission Info
                    </button>
                    {commissionStatus && commissionStatus.text === 'Needs Submission' && (
                      <button
                        className="btn btn-success"
                        onClick={() => navigate('/agent/commissions')}
                      >
                        Submit Commission
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {filteredItineraries.length === 0 && (
        <div className="no-itineraries">
          <p>No itineraries found.</p>
        </div>
      )}

      {/* Edit Modal */}
      <EditItineraryModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedItinerary(null);
        }}
        itinerary={selectedItinerary}
        onUpdate={() => {
          fetchItineraries();
          setShowEditModal(false);
          setSelectedItinerary(null);
        }}
      />

      {/* Add Commission Modal */}
      <AddCommissionModal
        isOpen={showCommissionModal}
        onClose={() => {
          setShowCommissionModal(false);
          setSelectedItinerary(null);
        }}
        itinerary={selectedItinerary}
        onUpdate={() => {
          fetchItineraries();
          setShowCommissionModal(false);
          setSelectedItinerary(null);
        }}
      />

      {/* Send Update Modal */}
      {showSendUpdateModal && (
        <div className="modal-overlay" onClick={() => setShowSendUpdateModal(false)}>
          <div className="modal-content send-update-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Send Update to Client</h2>
              <button className="close-button" onClick={() => setShowSendUpdateModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-info">
                Describe the changes you've made to the itinerary. This message will be sent to the client.
              </p>
              <div className="form-group">
                <label>Update Message *</label>
                <textarea
                  value={updateMessage}
                  onChange={(e) => setUpdateMessage(e.target.value)}
                  rows="4"
                  className="form-input"
                  placeholder="e.g., Updated your hotel to a suite with ocean view..."
                  required
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setShowSendUpdateModal(false)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={handleSendUpdateSubmit}
                disabled={sendingUpdate || !updateMessage.trim()}
              >
                {sendingUpdate ? 'Sending...' : 'Send Update'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItinerariesList;
